import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from "@angular/core";

import {ReactiveFormsModule} from "@angular/forms";
import {NachrichtenTypeEnum} from "../../../core/model/nachricht/nachrichten-type.enum";
import {PagesEnum} from "../../../core/model/pages.enum";
import {AnliegenMap} from "../../../core/model/anliegen-map";
import {DienststellenMap} from "../../../core/model/dienststellen-map";
import {Dienststelle} from "../../../core/model/dienststelle";
import {Anliegen, Subanliegen} from "../../../core/model/anliegen";
import i18next from "i18next";
import {FooterButton, FooterButtonType} from "../../../shared/components/footer/footer-button";
import {NachrichtPayload} from "../../../core/model/nachricht/nachricht";
import {FooterComponent} from "../../../shared/components/footer/footer.component";
import {HeaderComponent} from "../../../core/components/header/header.component";
import {I18NextPipe} from "@kokos-i18next";
import {AnliegenSelectorComponent} from "./anliegen-selector/anliegen-selector.component";
import {NavigationService} from "../../../shared/utils/navigation.service";
import {vNiveauEnum} from "../../../core/model/nachricht/vniveau.enum";
import {AnsprechpartnerComponent} from "./ansprechpartner/ansprechpartner.component";
import {DienststellenSelectorComponent} from "./dienststellen-selector/dienststellen-selector.component";
import {finalize, Subject, takeUntil} from "rxjs";
import {AnliegenService} from "../../../core/services/apiservices/anliegen.service";
import {DienststellenService} from "../../../core/services/apiservices/dienststellen.service";
import {HinweisboxTypeEnum} from "../../../shared/components/hinweisbox/hinweisbox-type.enum";
import {HinweiseComponent} from "./hinweise/hinweise.component";
import {RedirectService} from "../../../core/services/redirect.service";

@Component({
    selector: 'kokos-nachricht-prepare-step',
    standalone: true,
    imports: [ReactiveFormsModule, DienststellenSelectorComponent, AnliegenSelectorComponent, FooterComponent, HeaderComponent, I18NextPipe, AnsprechpartnerComponent, HinweiseComponent],
    templateUrl: './nachricht-prepare-step.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class NachrichtPrepareStepComponent implements OnInit, OnDestroy {

    @Input() anliegenMap: AnliegenMap;
    @Input() dienststellenMap: DienststellenMap;

    @Output() nachrichtPayload = new EventEmitter<NachrichtPayload>();
    @Output() anliegenName = new EventEmitter<string>();
    @Output() subanliegenChanged = new EventEmitter<Subanliegen>();

    dienststelle: Dienststelle | null = null;

    anliegenList: Anliegen[] = [];
    subanliegenList: Subanliegen[] = [];

    anliegen: Anliegen | null = null;
    subanliegen: Subanliegen | null = null;

    nachrichtenType: NachrichtenTypeEnum;

    loading = false;

    private readonly destroyed$ = new Subject<void>();

    protected readonly NachrichtenTypeEnum = NachrichtenTypeEnum;
    protected readonly PagesEnum = PagesEnum;
    protected readonly HinweisboxTypeEnum = HinweisboxTypeEnum;

    private readonly nav = inject(NavigationService);
    private readonly anliegenService = inject(AnliegenService);
    private readonly dienststellenService = inject(DienststellenService);
    private readonly redirectService = inject(RedirectService);

    redirectUrl: string;

    ngOnInit(): void {
        this.redirectUrl = this.redirectService.redirectUrl;
        if (!this.hasMultipleDienststellen()) {
            // assuming only Arbeitsagentur or only Jobcenter
            this.onNachrichtenTypeChanged(Object.keys(this.anliegenMap)[0] as NachrichtenTypeEnum.AA | NachrichtenTypeEnum.JC);
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    get forwardButtonConfig(): FooterButton {
        return {
            label: i18next.t('navigation.forward'),
            type: FooterButtonType.forward,
            showArrow: true
        };
    }

    get backButtonConfig(): FooterButton {
        return {
            label: i18next.t('navigation.cancel'),
            type: FooterButtonType.back,
        };
    }

    hasMultipleDienststellen(): boolean {
        if (!this.dienststellenMap) return false;
        const dienststellen = Object.values(this.dienststellenMap).filter(d => d != null);
        return dienststellen.length > 1;
    }

    onNachrichtenTypeChanged(nachrichtenType: NachrichtenTypeEnum.AA | NachrichtenTypeEnum.JC): void {
        this.nachrichtenType = nachrichtenType;

        this.dienststelle = this.dienststellenMap[nachrichtenType] ?? null;

        this.anliegenList = this.anliegenMap[nachrichtenType] ?? [];

        this.selectOrClearAnliegen();
    }

    onDienststelleChanged(dienststellenNummer: string) {
        this.loading = true;
        this.dienststellenService.getDienststelle(dienststellenNummer)
            .pipe(
                takeUntil(this.destroyed$))
            .subscribe((dienststelle) => {
                this.dienststelle = dienststelle;

                if (!this.dienststelle.teilnehmend) {
                    this.loading = false;
                    this.resetSelections();
                    return;
                }

                this.anliegenService.getAnliegen(this.nachrichtenType, dienststellenNummer)
                    .pipe(
                        finalize(() => this.loading = false),
                        takeUntil(this.destroyed$))
                    .subscribe((anliegenList) => {
                        this.anliegenList = anliegenList;

                        this.selectOrClearAnliegen();
                    });
            });
    }

    selectOrClearAnliegen(): void {
        if (this.anliegenList.length === 1) {
            this.onAnliegenChanged(this.anliegenList[0].id);
        } else {
            this.resetSelections();
        }
    }

    onAnliegenChanged(anliegenId: string): void {
        const selected = this.anliegenList.find(a => a.id == anliegenId) ?? null;

        this.anliegen = selected;
        if (this.anliegen) {
            this.anliegenName.emit(this.anliegen.name);
        }
        this.subanliegenList = selected?.subanliegen ?? [];

        if (this.subanliegenList.length === 1) {
            this.onSubanliegenChanged(this.subanliegenList[0].id);
        } else {
            this.subanliegen = null;
        }
    }

    onSubanliegenChanged(subanliegenId: string): void {
        const selected = this.subanliegenList.find(sub => sub.id == subanliegenId);
        if (selected) {
            this.subanliegen = selected;
            this.subanliegenChanged.emit(this.subanliegen);
        }
    }

    isReadyToProceed(): boolean {
        return !!(
            this.nachrichtenType &&
            this.dienststelle?.teilnehmend &&
            this.subanliegen?.nachrichtErstellen
        );
    }

    handleForwardClicked(): void {
        this.nachrichtPayload.emit(this.createNachricht());
    }

    handleBackClicked(): void {
        if (this.redirectUrl) {
            this.redirectTo(this.redirectUrl);
        } else {
            this.nav.goTo('/');
        }
    }


    get showAnliegen(): boolean {
        return this.anliegenList.length && this.dienststelle?.teilnehmend;
    }

    private createNachricht(): NachrichtPayload {
        return {
            vNiveau: vNiveauEnum.DEFAULT,
            bezug: this.subanliegen.bezug,
            empfaengerId: this.dienststelle.dienststellenNummer,
            empfaengerName: this.dienststelle.dienststellenName
        } as NachrichtPayload;
    }

    private resetSelections(): void {
        this.anliegen = null;
        this.subanliegen = null;
        this.subanliegenList = [];
    }

    protected redirectTo(url: string): void {
        globalThis.location.href = url;
    }

}